import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { PhotoMedia, PhotoPost, Release, Track } from '@/types';
import {
  artworkUrl,
  fetchRadio,
  mediaSessionArtwork,
  mediaUrl,
  preloadImageUrl,
} from '@/lib/api';
import {
  audioTakesOverFromVideo,
  isVideoSessionActive,
  VIDEO_SESSION_EVENT,
} from '@/lib/videoSession';
import { cacheAudioUrl, isInstalledPwa } from '@/lib/audioOffline';
import {
  applyRadioElementLoudness,
  clearRadioElementLoudness,
  ensureAudioFx,
  hasAudioFxGraph,
  resumeAudioFx,
} from '@/lib/audioFx';
import {
  attachRadioHls,
  detachRadioHls,
  radioHlsSupported,
  radioPlaylistUrl,
} from '@/lib/radioHls';
import {
  DEFAULT_CLOCK_TZ,
  RADIO_DAY_DURATION_SEC,
  RADIO_DRIFT_RELOAD_SEC,
  RADIO_SKIP_STEP_MS,
  RADIO_TIMESHIFT_MAX_MS,
  normalizeClockTimezone,
  radioPlayNowMs,
  resolveRadioPosition,
  secondsSinceMidnight,
  type RadioSchedule,
  type RadioTrack,
} from '@/lib/radioSchedule';
import {
  leaveRadioPresence,
  RADIO_PRESENCE_INTERVAL_MS,
  touchRadioPresence,
} from '@/lib/radioPresence';

function createPlayerAudio(): HTMLAudioElement {
  const audio = new Audio();
  audio.crossOrigin = 'anonymous';
  audio.preload = 'auto';
  audio.setAttribute('playsinline', 'true');
  audio.setAttribute('webkit-playsinline', 'true');
  document.body.appendChild(audio);
  return audio;
}

function ensureAudioElement(
  audioRef: React.MutableRefObject<HTMLAudioElement | null>,
): HTMLAudioElement {
  let audio = audioRef.current;
  if (!audio) {
    audio = createPlayerAudio();
    audioRef.current = audio;
  } else if (!audio.crossOrigin) {
    audio.crossOrigin = 'anonymous';
  }
  return audio;
}

/**
 * Live radio must use an element that Web Audio has never captured.
 * createMediaElementSource + AudioContext dies on iOS lock screen.
 */
function ensureRadioAudioElement(
  albumAudioRef: React.MutableRefObject<HTMLAudioElement | null>,
  radioAudioRef: React.MutableRefObject<HTMLAudioElement | null>,
  publicAudioRef: React.MutableRefObject<HTMLAudioElement | null>,
): HTMLAudioElement {
  // Pause album element so only one stream is audible.
  const album = albumAudioRef.current;
  if (album && !album.paused) {
    try {
      album.pause();
    } catch {
      /* ignore */
    }
  }

  let radio = radioAudioRef.current;
  if (!radio) {
    radio = createPlayerAudio();
    radioAudioRef.current = radio;
  }
  // If somehow FX already owns this node, mint a fresh one.
  if (hasAudioFxGraph() && radio === album) {
    radio = createPlayerAudio();
    radioAudioRef.current = radio;
  }
  publicAudioRef.current = radio;
  return radio;
}

/**
 * Radio Media Session scrubber as a 24h day clock (matches desktop HH:MM → 24:00).
 * Avoids the HLS day-loop’s multi-hour VOD duration leaking onto the lock screen
 * when Infinity is rejected. Call sparingly — not on a 1Hz timer (macOS art thrash).
 */
function publishRadioDayPositionState(positionSec: number) {
  if (!('mediaSession' in navigator)) return;
  const duration = RADIO_DAY_DURATION_SEC;
  const position = Math.min(Math.max(0, positionSec), duration - 0.001);
  try {
    navigator.mediaSession.setPositionState({
      duration,
      playbackRate: 1,
      position,
    });
  } catch {
    try {
      navigator.mediaSession.setPositionState();
    } catch {
      /* unsupported */
    }
  }
}

type PositionCache = { duration: number; position: number };

/**
 * Finite Media Session scrubber for on-demand releases.
 * Prefer catalog duration; cache last good values so iOS lock screen still has
 * m:ss after pause / freeze when the element briefly reports NaN.
 */
function publishOnDemandPositionState(
  audio: HTMLAudioElement | null | undefined,
  track: Track | undefined,
  cache?: { current: PositionCache | null },
) {
  if (!('mediaSession' in navigator)) return;
  const catalog = track?.duration;
  const fromAudio = audio && Number.isFinite(audio.duration) ? audio.duration : NaN;
  let duration =
    Number.isFinite(catalog) && (catalog as number) > 0
      ? (catalog as number)
      : Number.isFinite(fromAudio) && fromAudio > 0
        ? fromAudio
        : cache?.current?.duration ?? NaN;
  if (!Number.isFinite(duration) || duration <= 0) return;
  const rawPos = audio ? audio.currentTime || 0 : cache?.current?.position ?? 0;
  const position = Math.min(Math.max(0, rawPos), duration);
  if (cache) cache.current = { duration, position };
  try {
    // Spec forbids playbackRate 0; paused is signaled via playbackState.
    // UA extrapolates with rate 0 while playbackState === 'paused'.
    navigator.mediaSession.setPositionState({
      duration,
      playbackRate: audio?.playbackRate || 1,
      position,
    });
  } catch {
    /* unsupported */
  }
}

export type MenuMode = 'music' | 'photo' | 'info' | 'settings';
export type RepeatMode = 'off' | 'all' | 'one';

type ReleasePlaybackOpts = {
  tracks: Track[];
  /** Catalog release id for share / deep links. */
  releaseId: string;
  albumName: string;
  artist?: string;
  type: string;
  artwork: string;
  credits?: Release['credits'];
  streamLinks?: Release['streamLinks'];
};

type PlayerContextValue = {
  audioRef: React.RefObject<HTMLAudioElement | null>;
  tracks: Track[];
  currentIndex: number;
  /** Active on-demand release id (empty in live until a source id is known). */
  releaseId: string;
  albumName: string;
  artist: string;
  releaseType: string;
  artwork: string;
  /** Credits for the current on-demand release (not live radio). */
  credits: Release['credits'];
  /** Streaming store links for the current on-demand release. */
  streamLinks: Release['streamLinks'];
  playing: boolean;
  /** True after first play for the current queue; stays true while paused. */
  sessionActive: boolean;
  shuffleMode: boolean;
  repeatMode: RepeatMode;
  /** Pseudo-live catalog radio — all listeners share a UTC schedule. */
  radioMode: boolean;
  /** True while live radio is fetching / seeking / waiting for audio. */
  radioBuffering: boolean;
  /** Ms behind the live playhead (0 = locked to live). */
  radioTimeshiftMs: number;
  /** IANA timezone for the live footer clock (from site info / radio schedule). */
  radioClockTimezone: string;
  setReleasePlayback: (opts: ReleasePlaybackOpts) => void;
  /**
   * Play/pause by track position in the active queue.
   * Pass `adoptPending: true` when the user taps a track on a browsed release
   * (TrackList / deep-link) so a pending album can replace the session.
   * Session chrome (sheet, skip, mini) must omit that flag — matching by
   * position alone would steal playback onto the browsed release’s same #.
   */
  toggleTrack: (
    position: number,
    opts?: { shuffle?: boolean; adoptPending?: boolean },
  ) => void;
  /** Play/pause album: resume current session, or start from first playable track. */
  playAlbum: () => void;
  /** Start playback from a random playable track; next track stays shuffled. */
  shufflePlay: () => void;
  toggleShuffle: () => void;
  cycleRepeat: () => void;
  playNext: () => void;
  playPrevious: () => void;
  /** Tune into the shared live radio (or rejoin live if already in radio mode). */
  startRadio: () => Promise<void>;
  /** Jump radio playhead back to the live schedule point. */
  jumpToLive: () => void;
  currentTime: number;
  /** Seek the active element and sync UI time (safe during scrub). */
  seek: (timeSec: number) => void;
  /**
   * While true, timeupdate will not push React currentTime (avoids thumb fight
   * during range scrub on touch/mouse).
   */
  setSeekScrubbing: (scrubbing: boolean) => void;
};

const PlayerContext = createContext<PlayerContextValue | null>(null);

function isPlayable(track: Track): boolean {
  return track.duration !== 0 && Boolean(track.streamUrl?.trim());
}

function radioTracksToQueue(radioTracks: RadioTrack[]): Track[] {
  return radioTracks.map((t, i) => ({
    id: t.id,
    name: t.name,
    duration: t.duration,
    // Keep catalog track position for share deep-links (`?track=`).
    position: Number(t.position) || i + 1,
    streamUrl: t.streamUrl,
    artist: t.artist || undefined,
    releaseId: t.releaseId || undefined,
    albumName: t.albumName || undefined,
    releaseType: t.releaseType || undefined,
    replayGain: t.replayGain,
    replayGainTrack: t.replayGainTrack,
    replayGainAlbum: t.replayGainAlbum,
  }));
}

/**
 * Within this window, Previous skips to the prior track; past it, Previous
 * restarts the current track (Apple Music / Spotify convention).
 */
const PREV_RESTART_THRESHOLD_SEC = 2;

/** Next/prev playable neighbor in the queue (optional wrap for repeat). */
function findNeighborPlayable(
  tracks: Track[],
  fromIndex: number,
  direction: 1 | -1,
  wrap: boolean,
): Track | null {
  if (!tracks.length || fromIndex < 0 || fromIndex >= tracks.length) return null;
  const len = tracks.length;
  let i = fromIndex;
  for (let step = 0; step < len - 1; step++) {
    i += direction;
    if (i < 0 || i >= len) {
      if (!wrap) return null;
      i = direction === 1 ? 0 : len - 1;
    }
    if (i === fromIndex) return null;
    const track = tracks[i];
    if (track && isPlayable(track)) return track;
  }
  return null;
}

type PrefetchSlot = {
  url: string;
  el: HTMLAudioElement;
};

function warmPrefetchSlot(
  slotRef: React.MutableRefObject<PrefetchSlot | null>,
  url: string,
  skipUrl?: string,
): void {
  if (!url || (skipUrl && url === skipUrl)) {
    return;
  }
  let slot = slotRef.current;
  if (!slot) {
    const el = new Audio();
    el.preload = 'auto';
    el.crossOrigin = 'anonymous';
    el.muted = true;
    slot = { url: '', el };
    slotRef.current = slot;
  }
  if (slot.url === url) return;
  slot.url = url;
  slot.el.src = url;
  try {
    slot.el.load();
  } catch {
    /* ignore */
  }
}

function clearPrefetchSlot(slotRef: React.MutableRefObject<PrefetchSlot | null>): void {
  const slot = slotRef.current;
  if (!slot) return;
  slot.url = '';
  try {
    slot.el.removeAttribute('src');
    slot.el.load();
  } catch {
    /* ignore */
  }
}

/** Store a full copy in the PWA offline audio bucket (on-demand / installed only). */
function rememberOfflineAudio(url: string): void {
  if (!url || !isInstalledPwa()) return;
  void cacheAudioUrl(url);
}

export function PlayerProvider({ children }: { children: ReactNode }) {
  /** Album/release element — Web Audio FX on desktop; native on iOS (lock-screen). */
  const albumAudioRef = useRef<HTMLAudioElement | null>(null);
  /** Dedicated radio element — never routed through Web Audio (iOS lock-screen). */
  const radioAudioRef = useRef<HTMLAudioElement | null>(null);
  /** Public ref: points at whichever element is currently driving playback. */
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [tracks, setTracks] = useState<Track[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const tracksRef = useRef(tracks);
  tracksRef.current = tracks;
  const currentIndexRef = useRef(currentIndex);
  currentIndexRef.current = currentIndex;
  const [releaseId, setReleaseId] = useState('');
  const [albumName, setAlbumName] = useState('');
  const [artist, setArtist] = useState('');
  const [releaseType, setReleaseType] = useState('');
  const [artwork, setArtwork] = useState('');
  const [credits, setCredits] = useState<Release['credits']>(undefined);
  const [streamLinks, setStreamLinks] = useState<Release['streamLinks']>(undefined);
  const seekScrubbingRef = useRef(false);
  /**
   * Target of the latest on-demand seek. Desktop often applies in-buffer seeks
   * without flipping `audio.seeking`, so timeupdate can still report the old
   * playhead and jump the footer timeline after a scrub drop.
   */
  const pendingSeekTargetRef = useRef<number | null>(null);
  const pendingSeekClearTimerRef = useRef<number | null>(null);
  const [playing, setPlaying] = useState(false);
  const [sessionActive, setSessionActive] = useState(false);
  const sessionActiveRef = useRef(false);
  sessionActiveRef.current = sessionActive;
  const [shuffleMode, setShuffleMode] = useState(false);
  const [repeatMode, setRepeatMode] = useState<RepeatMode>('off');
  const [currentTime, setCurrentTime] = useState(0);
  const [radioMode, setRadioMode] = useState(false);
  const [radioBuffering, setRadioBuffering] = useState(false);
  const [radioTimeshiftMs, setRadioTimeshiftMs] = useState(0);
  const [radioClockTimezone, setRadioClockTimezone] =
    useState(DEFAULT_CLOCK_TZ);
  const repeatModeRef = useRef<RepeatMode>('off');
  repeatModeRef.current = repeatMode;
  const radioModeRef = useRef(false);
  radioModeRef.current = radioMode;
  const radioTimeshiftRef = useRef(0);
  radioTimeshiftRef.current = radioTimeshiftMs;
  const radioSkewRef = useRef(0);
  const radioClockTimezoneRef = useRef(DEFAULT_CLOCK_TZ);
  radioClockTimezoneRef.current = radioClockTimezone;
  const radioScheduleRef = useRef<RadioSchedule | null>(null);
  const pendingReleaseRef = useRef<ReleasePlaybackOpts | null>(null);
  const prevPrefetchRef = useRef<PrefetchSlot | null>(null);
  const nextPrefetchRef = useRef<PrefetchSlot | null>(null);
  const lastPositionStateRef = useRef(0);
  /** Last successful finite position — re-assert when iOS drops lock-screen times. */
  const positionCacheRef = useRef<PositionCache | null>(null);
  /** Dedupes MediaMetadata so macOS doesn’t reload artwork every sync. */
  const lastMediaMetaRef = useRef<string>('');
  /** Last radio day-clock arm key (schedule track id) — skip redundant posts. */
  const livePositionArmedRef = useRef('');
  /** Schedule index currently loaded into the radio element (vs React currentIndex). */
  const radioLoadedIndexRef = useRef(-1);
  /** True while loadRadioAtPlayhead is in flight — blocks tick/wake re-entry. */
  const radioLoadInFlightRef = useRef(false);
  /** Tab was hidden; only then may wake rejoin the schedule (not every window focus). */
  const radioWasHiddenRef = useRef(false);
  /** Live audio is on the HLS day-loop (lock-screen advances without JS src swaps). */
  const radioHlsActiveRef = useRef(false);
  /** Timeshift baked into the current HLS playlist URL. */
  const radioHlsShiftRef = useRef<number | null>(null);
  const wantPlayingRef = useRef(false);
  const radioBufferingRef = useRef(false);
  /** Delay BUFFERING chrome so brief live track stitches don’t flash the label. */
  const radioBufferingWaitTimerRef = useRef<number | null>(null);
  /**
   * Progressive next-track (or same-session) src swap in progress — keep the
   * LIVE badge static; waiting/stalled during the stitch is expected, not a drop.
   */
  const radioSeamlessStitchRef = useRef(false);
  /** Ends stitch suppression if the new file never becomes ready (real drop). */
  const radioSeamlessStitchTimerRef = useRef<number | null>(null);

  /**
   * Sync Media Session scrubber:
   * - Radio → 24h day clock (seconds since midnight → 86400), once per track key
   * - On-demand → finite catalog duration + position (kept while paused)
   */
  const syncMediaPosition = useCallback(
    (audio?: HTMLAudioElement | null, opts?: { forceLive?: boolean }) => {
      const el = audio ?? audioRef.current;
      if (radioModeRef.current) {
        positionCacheRef.current = null;
        // Track changes publish via clearLivePositionState; skip redundant posts
        // unless play/pause/seek/metadata forces a refresh.
        if (!opts?.forceLive && livePositionArmedRef.current) return;
        const playNow = radioPlayNowMs(
          radioSkewRef.current,
          radioTimeshiftRef.current,
        );
        publishRadioDayPositionState(
          secondsSinceMidnight(playNow, radioClockTimezoneRef.current),
        );
        if (!livePositionArmedRef.current) {
          livePositionArmedRef.current = 'day';
        }
        return;
      }
      livePositionArmedRef.current = '';
      publishOnDemandPositionState(
        el,
        tracksRef.current[currentIndexRef.current],
        positionCacheRef,
      );
    },
    [],
  );

  const publishFinitePosition = syncMediaPosition;

  const clearPendingSeek = useCallback((match?: number) => {
    if (
      match !== undefined &&
      pendingSeekTargetRef.current !== null &&
      Math.abs(pendingSeekTargetRef.current - match) > 0.01
    ) {
      return;
    }
    pendingSeekTargetRef.current = null;
    if (pendingSeekClearTimerRef.current != null) {
      window.clearTimeout(pendingSeekClearTimerRef.current);
      pendingSeekClearTimerRef.current = null;
    }
  }, []);

  const armPendingSeek = useCallback(
    (timeSec: number, audio: HTMLAudioElement) => {
      pendingSeekTargetRef.current = timeSec;
      if (pendingSeekClearTimerRef.current != null) {
        window.clearTimeout(pendingSeekClearTimerRef.current);
      }
      const settle = () => clearPendingSeek(timeSec);
      audio.addEventListener('seeked', settle, { once: true });
      // In-buffer seeks on desktop Chrome often never fire `seeked`.
      pendingSeekClearTimerRef.current = window.setTimeout(settle, 500);
    },
    [clearPendingSeek],
  );

  const setSeekScrubbing = useCallback(
    (scrubbing: boolean) => {
      seekScrubbingRef.current = scrubbing;
      // Do not snap React time from audio.currentTime here — during playback
      // the engine can still report the pre-seek playhead and jump the UI.
      // seek() already wrote the drop target; only refresh Media Session.
      if (scrubbing || !audioRef.current) return;
      const audio = audioRef.current;
      const publish = () => {
        if (seekScrubbingRef.current) return;
        publishFinitePosition(audio, { forceLive: true });
      };
      if (audio.seeking || pendingSeekTargetRef.current != null) {
        audio.addEventListener('seeked', publish, { once: true });
        window.setTimeout(publish, 500);
        return;
      }
      publish();
    },
    [publishFinitePosition],
  );

  const seek = useCallback(
    (timeSec: number) => {
      const audio = audioRef.current;
      if (!audio || !Number.isFinite(timeSec)) return;
      armPendingSeek(timeSec, audio);
      try {
        audio.currentTime = timeSec;
      } catch {
        clearPendingSeek(timeSec);
      }
      setCurrentTime(timeSec);
      publishFinitePosition(audio, { forceLive: true });
    },
    [armPendingSeek, clearPendingSeek, publishFinitePosition],
  );

  const clearLivePositionState = useCallback((trackKey = 'live') => {
    // New schedule track → allow a fresh day-clock publish.
    livePositionArmedRef.current = '';
    const playNow = radioPlayNowMs(
      radioSkewRef.current,
      radioTimeshiftRef.current,
    );
    publishRadioDayPositionState(
      secondsSinceMidnight(playNow, radioClockTimezoneRef.current),
    );
    livePositionArmedRef.current = trackKey;
  }, []);

  const useAlbumAudio = useCallback(() => {
    const album = ensureAudioElement(albumAudioRef);
    const radio = radioAudioRef.current;
    if (radio && !radio.paused) {
      try {
        radio.pause();
      } catch {
        /* ignore */
      }
    }
    clearRadioElementLoudness(radio);
    clearRadioElementLoudness(album);
    audioRef.current = album;
    return album;
  }, []);

  const applyReleasePlayback = useCallback((opts: ReleasePlaybackOpts) => {
    pendingReleaseRef.current = null;
    radioScheduleRef.current = null;
    if (radioModeRef.current) leaveRadioPresence();
    radioModeRef.current = false;
    radioTimeshiftRef.current = 0;
    wantPlayingRef.current = false;
    lastMediaMetaRef.current = '';
    livePositionArmedRef.current = '';
    radioHlsActiveRef.current = false;
    radioHlsShiftRef.current = null;
    setRadioMode(false);
    setRadioBuffering(false);
    setRadioTimeshiftMs(0);
    detachRadioHls();
    clearPrefetchSlot(prevPrefetchRef);
    clearPrefetchSlot(nextPrefetchRef);
    setTracks(opts.tracks);
    setReleaseId(opts.releaseId || '');
    setAlbumName(opts.albumName);
    setArtist(opts.artist || '');
    setReleaseType(opts.type);
    setArtwork(opts.artwork);
    setCredits(opts.credits);
    setStreamLinks(opts.streamLinks);
    setCurrentIndex(0);
    setCurrentTime(0);
    setSessionActive(false);
    setPlaying(false);
    setShuffleMode(false);
    setRepeatMode('off');
    const radio = radioAudioRef.current;
    if (radio) {
      try {
        radio.pause();
        radio.removeAttribute('src');
        radio.load();
      } catch {
        /* ignore */
      }
    }
    const audio = useAlbumAudio();
    audio.pause();
    audio.removeAttribute('src');
    audio.load();
  }, [useAlbumAudio]);

  const setReleasePlayback = useCallback(
    (opts: ReleasePlaybackOpts) => {
      // Browsing another release while live / mid-album must not tear down playback.
      // Queue swaps when the user hits Play / a track on the browsed release.
      if (radioModeRef.current || sessionActiveRef.current) {
        pendingReleaseRef.current = opts;
        return;
      }
      applyReleasePlayback(opts);
    },
    [applyReleasePlayback],
  );

  const toggleShuffle = useCallback(() => {
    if (radioModeRef.current) return;
    setShuffleMode((prev) => !prev);
  }, []);

  const cycleRepeat = useCallback(() => {
    if (radioModeRef.current) return;
    setRepeatMode((prev) =>
      prev === 'off' ? 'all' : prev === 'all' ? 'one' : 'off',
    );
  }, []);

  const updateMediaSession = useCallback(
    (track: Track, opts?: { album?: string; artist?: string; artwork?: string }) => {
      if (!('mediaSession' in navigator)) return;
      const sessionAlbum = opts?.album ?? albumName;
      // Track artist always wins over release artist (incl. collaborations).
      const sessionArtist =
        track.artist?.trim() ||
        opts?.artist?.trim() ||
        artist?.trim() ||
        'Zachary Gray';
      const artworkEntries = mediaSessionArtwork(opts?.artwork ?? artwork);
      const artSrc = artworkEntries[0]?.src ?? '';
      const artType = artworkEntries[0]?.type ?? '';
      const metaKey = `${track.name}\0${sessionArtist}\0${sessionAlbum}\0${artSrc}\0${artType}`;
      if (lastMediaMetaRef.current === metaKey) return;
      lastMediaMetaRef.current = metaKey;
      // Metadata only — play/pause handlers own playbackState (avoids macOS art reload).
      navigator.mediaSession.metadata = new MediaMetadata({
        title: track.name,
        artist: sessionArtist,
        album: sessionAlbum,
        artwork: artworkEntries,
      });
    },
    [albumName, artist, artwork],
  );

  const syncRadioUi = useCallback(
    (schedule: RadioSchedule, index: number) => {
      const radioTrack = schedule.tracks[index];
      if (!radioTrack) return;
      const queue = radioTracksToQueue(schedule.tracks);
      const trackKey = radioTrack.id || `${index}:${radioTrack.name}`;
      setTracks(queue);
      setCurrentIndex(index);
      setReleaseId(radioTrack.releaseId || '');
      const sessionAlbum = radioTrack.albumName?.trim() || '';
      setAlbumName(sessionAlbum);
      setArtist(radioTrack.artist || 'Zachary Gray');
      setReleaseType('radio');
      setArtwork(radioTrack.artworkUrl || '');
      updateMediaSession(queue[index]!, {
        album: sessionAlbum,
        artist: radioTrack.artist || 'Zachary Gray',
        artwork: radioTrack.artworkUrl || '',
      });
      // Refresh day-clock scrubber once per schedule track.
      clearLivePositionState(trackKey);
    },
    [clearLivePositionState, updateMediaSession],
  );

  /** Seek before play so live never starts at 0 then jumps. */
  const seekRadioReady = useCallback(
    async (audio: HTMLAudioElement, offsetSec: number) => {
      if (!Number.isFinite(offsetSec) || offsetSec <= 0) {
        setCurrentTime(audio.currentTime || 0);
        return;
      }
      if (audio.readyState < 1) {
        await new Promise<void>((resolve) => {
          const done = () => resolve();
          audio.addEventListener('loadedmetadata', done, { once: true });
          audio.addEventListener('error', done, { once: true });
          window.setTimeout(done, 4000);
        });
      }
      if (Math.abs((audio.currentTime || 0) - offsetSec) < 0.35) {
        setCurrentTime(audio.currentTime);
        return;
      }
      await new Promise<void>((resolve) => {
        let settled = false;
        const finish = () => {
          if (settled) return;
          settled = true;
          audio.removeEventListener('seeked', finish);
          resolve();
        };
        audio.addEventListener('seeked', finish);
        try {
          audio.currentTime = offsetSec;
        } catch {
          finish();
          return;
        }
        window.setTimeout(finish, 2500);
      });
      setCurrentTime(audio.currentTime);
    },
    [],
  );

  /**
   * Advance live chrome / Media Session to the current schedule slot without
   * touching playback. Used while the user has radio paused.
   */
  const syncRadioMetadata = useCallback(
    (opts?: { timeshiftMs?: number }) => {
      const schedule = radioScheduleRef.current;
      if (!schedule?.tracks?.length) return null;

      const shift = Math.max(
        0,
        Math.min(
          RADIO_TIMESHIFT_MAX_MS,
          opts?.timeshiftMs ?? radioTimeshiftRef.current,
        ),
      );
      radioTimeshiftRef.current = shift;
      setRadioTimeshiftMs(shift);

      const playNow = radioPlayNowMs(radioSkewRef.current, shift);
      const pos = resolveRadioPosition(
        schedule.tracks,
        schedule.epochMs,
        playNow,
      );
      if (!pos) return null;

      radioModeRef.current = true;
      setRadioMode(true);
      syncRadioUi(schedule, pos.index);
      syncMediaPosition(audioRef.current, { forceLive: true });
      return { schedule, pos, shift };
    },
    [syncRadioUi, syncMediaPosition],
  );

  const loadRadioAtPlayhead = useCallback(
    async (opts?: { timeshiftMs?: number; forceReload?: boolean }) => {
      const schedule = radioScheduleRef.current;
      if (!schedule?.tracks?.length) {
        setRadioBuffering(false);
        radioBufferingRef.current = false;
        return;
      }
      if (radioLoadInFlightRef.current) return;
      radioLoadInFlightRef.current = true;

      const prevShift = radioTimeshiftRef.current;
      const shift = Math.max(
        0,
        Math.min(
          RADIO_TIMESHIFT_MAX_MS,
          opts?.timeshiftMs ?? radioTimeshiftRef.current,
        ),
      );
      const timeshiftChanging =
        opts?.timeshiftMs !== undefined && shift !== prevShift;
      radioTimeshiftRef.current = shift;
      setRadioTimeshiftMs(shift);

      // Show live chrome immediately — never flash the album scrubber first.
      radioModeRef.current = true;
      setRadioMode(true);
      // Already on air + same delay: next-track stitch — keep LIVE, not BUFFERING.
      const seamlessAdvance =
        sessionActiveRef.current &&
        wantPlayingRef.current &&
        radioLoadedIndexRef.current >= 0 &&
        !timeshiftChanging;
      radioSeamlessStitchRef.current = seamlessAdvance;
      if (radioSeamlessStitchTimerRef.current != null) {
        window.clearTimeout(radioSeamlessStitchTimerRef.current);
        radioSeamlessStitchTimerRef.current = null;
      }
      if (seamlessAdvance) {
        // Cancel any pending stall→BUFFERING so the badge never blinks on stitch.
        if (radioBufferingWaitTimerRef.current != null) {
          window.clearTimeout(radioBufferingWaitTimerRef.current);
          radioBufferingWaitTimerRef.current = null;
        }
        // If the new MP3 never becomes ready, allow stall chrome again.
        radioSeamlessStitchTimerRef.current = window.setTimeout(() => {
          radioSeamlessStitchTimerRef.current = null;
          radioSeamlessStitchRef.current = false;
        }, 12_000);
      } else {
        radioBufferingRef.current = true;
        setRadioBuffering(true);
      }

      try {
        const playNow = radioPlayNowMs(radioSkewRef.current, shift);
        const pos = resolveRadioPosition(
          schedule.tracks,
          schedule.epochMs,
          playNow,
        );
        if (!pos) {
          radioBufferingRef.current = false;
          setRadioBuffering(false);
          return;
        }

        // Never attach Web Audio to the radio element — AudioContext suspends on
        // iOS lock screen and kills progressive MP3 playback.
        const audio = ensureRadioAudioElement(
          albumAudioRef,
          radioAudioRef,
          audioRef,
        );

        // Queue + session together so footer never paints a stale track.
        radioLoadedIndexRef.current = pos.index;
        syncRadioUi(schedule, pos.index);
        setSessionActive(true);
        // Always level live — native volume (works on iOS lock screen; ignores Normalize).
        applyRadioElementLoudness(audio, {
          id: pos.track.id,
          name: pos.track.name,
          duration: pos.track.duration,
          position: pos.index + 1,
          streamUrl: pos.track.streamUrl,
          replayGain: pos.track.replayGain,
          replayGainTrack: pos.track.replayGainTrack,
          replayGainAlbum: pos.track.replayGainAlbum,
        });

        const loadProgressive = async () => {
          detachRadioHls();
          radioHlsActiveRef.current = false;
          radioHlsShiftRef.current = null;
          const trackUrl = mediaUrl(pos.track.streamUrl);
          const srcBase = (audio.src || '').split('#')[0] || '';
          const sameTrack =
            Boolean(srcBase) &&
            !srcBase.includes('.m3u8') &&
            mediaUrl(srcBase) === trackUrl;

          if (sameTrack) {
            const drift = Math.abs((audio.currentTime || 0) - pos.offsetSec);
            if (drift > 2.5) {
              await seekRadioReady(audio, pos.offsetSec);
            } else {
              setCurrentTime(audio.currentTime);
            }
          } else {
            const warm = nextPrefetchRef.current;
            if (warm?.url && mediaUrl(warm.url) === trackUrl && warm.el.src) {
              audio.src = warm.el.src;
              clearPrefetchSlot(nextPrefetchRef);
            } else {
              const t = Math.max(0, Math.floor(pos.offsetSec * 10) / 10);
              audio.src = t > 0.5 ? `${trackUrl}#t=${t}` : trackUrl;
            }
            await seekRadioReady(audio, pos.offsetSec);
          }
        };

        /**
         * Safari/iOS: native HLS day-loop (lock-screen advances without JS).
         * Chrome/Firefox: progressive MP3 — hls.js can't play whole-file MP3 segments.
         */
        const useHls = radioHlsSupported(audio);
        if (useHls) {
          const alreadyHls =
            radioHlsActiveRef.current &&
            radioHlsShiftRef.current === shift &&
            (audio.src || '').includes('/stream/radio') &&
            !audio.paused &&
            !audio.ended;
          if (alreadyHls && !opts?.forceReload) {
            radioBufferingRef.current = false;
            setRadioBuffering(false);
            wantPlayingRef.current = true;
            setPlaying(true);
            return;
          }

          const playlist = radioPlaylistUrl(shift, Boolean(opts?.forceReload));
          try {
            await attachRadioHls(audio, playlist);
            radioHlsActiveRef.current = true;
            radioHlsShiftRef.current = shift;
          } catch (hlsErr) {
            console.warn('radio HLS failed; falling back to progressive', hlsErr);
            await loadProgressive();
          }
        } else {
          await loadProgressive();
        }

        wantPlayingRef.current = true;
        try {
          await audio.play();
          setPlaying(true);
          setSessionActive(true);
          radioModeRef.current = true;
          setRadioMode(true);
          radioBufferingRef.current = false;
          setRadioBuffering(false);
        } catch (err) {
          // HLS src rejected (e.g. desktop Chrome) — recover with progressive MP3.
          if (radioHlsActiveRef.current) {
            console.warn('radio HLS play failed; falling back to progressive', err);
            try {
              await loadProgressive();
              await audio.play();
              setPlaying(true);
              setSessionActive(true);
              radioModeRef.current = true;
              setRadioMode(true);
              radioBufferingRef.current = false;
              setRadioBuffering(false);
              return;
            } catch (err2) {
              console.error(err2);
            }
          } else {
            console.error(err);
          }
          wantPlayingRef.current = false;
          setPlaying(false);
          radioBufferingRef.current = false;
          setRadioBuffering(false);
        }
      } finally {
        radioLoadInFlightRef.current = false;
      }
    },
    [seekRadioReady, syncRadioUi],
  );

  const jumpToLive = useCallback(() => {
    void loadRadioAtPlayhead({ timeshiftMs: 0 });
  }, [loadRadioAtPlayhead]);

  const skipRadioBy = useCallback(
    (deltaMs: number) => {
      // Positive delta = move playhead forward (toward live) → smaller timeshift.
      const next = radioTimeshiftRef.current - deltaMs;
      void loadRadioAtPlayhead({ timeshiftMs: next });
    },
    [loadRadioAtPlayhead],
  );

  const startRadio = useCallback(async () => {
    // Stop current audio — live button buffers while we fetch the schedule.
    for (const el of [albumAudioRef.current, radioAudioRef.current]) {
      if (!el) continue;
      try {
        el.pause();
      } catch {
        /* ignore */
      }
    }
    wantPlayingRef.current = false;
    setPlaying(false);
    setRadioBuffering(true);
    radioModeRef.current = true;
    setRadioMode(true);
    // Keep session + queue mounted across fetchRadio() so desktop footer /
    // mobile tray stay visible between on-demand ↔ live. syncRadioUi swaps
    // metadata when the schedule lands; failure restores radioMode below and
    // leaves any prior on-demand chrome intact.
    setShuffleMode(false);
    setRepeatMode('off');
    pendingReleaseRef.current = null;
    radioScheduleRef.current = null;
    radioTimeshiftRef.current = 0;
    setRadioTimeshiftMs(0);
    lastMediaMetaRef.current = '';
    livePositionArmedRef.current = '';
    radioLoadedIndexRef.current = -1;
    radioLoadInFlightRef.current = false;
    radioHlsActiveRef.current = false;
    radioHlsShiftRef.current = null;
    radioBufferingRef.current = true;
    detachRadioHls();

    try {
      // Fresh schedule + playhead only when starting (held in-session until stop).
      const schedule = await fetchRadio();
      radioSkewRef.current = schedule.serverNow - Date.now();
      radioScheduleRef.current = schedule;
      setRadioClockTimezone(normalizeClockTimezone(schedule.clockTimezone));
      // loadRadioAtPlayhead syncs queue + arms session in the same turn.
      audioTakesOverFromVideo();
      await loadRadioAtPlayhead({ timeshiftMs: 0, forceReload: true });
    } catch (err) {
      console.error(err);
      radioModeRef.current = false;
      setRadioMode(false);
      setRadioBuffering(false);
    }
  }, [loadRadioAtPlayhead]);

  const exitRadioToRelease = useCallback(
    (opts: ReleasePlaybackOpts, playPosition?: number, shuffle = false) => {
      pendingReleaseRef.current = null;
      radioScheduleRef.current = null;
      leaveRadioPresence();
      radioModeRef.current = false;
      radioTimeshiftRef.current = 0;
      wantPlayingRef.current = false;
      radioHlsActiveRef.current = false;
      radioHlsShiftRef.current = null;
      setRadioMode(false);
      setRadioBuffering(false);
      setRadioTimeshiftMs(0);
      detachRadioHls();
      clearPrefetchSlot(prevPrefetchRef);
      clearPrefetchSlot(nextPrefetchRef);
      setTracks(opts.tracks);
      setReleaseId(opts.releaseId || '');
      setAlbumName(opts.albumName);
      setArtist(opts.artist || '');
      setReleaseType(opts.type);
      setArtwork(opts.artwork);
      setCredits(opts.credits);
      setStreamLinks(opts.streamLinks);
      setShuffleMode(shuffle);
      setRepeatMode('off');

      const audio = useAlbumAudio();
      ensureAudioFx(audio);
      void resumeAudioFx();

      let index = playPosition == null
        ? opts.tracks.findIndex(isPlayable)
        : opts.tracks.findIndex((t) => t.position === playPosition);
      if (index < 0) index = opts.tracks.findIndex(isPlayable);
      const track = index >= 0 ? opts.tracks[index] : undefined;
      if (!track || !isPlayable(track)) {
        setCurrentIndex(0);
        setCurrentTime(0);
        setSessionActive(false);
        setPlaying(false);
        audio.pause();
        audio.removeAttribute('src');
        audio.load();
        return;
      }

      const startUrl = mediaUrl(track.streamUrl);
      audioTakesOverFromVideo();
      audio.src = startUrl;
      rememberOfflineAudio(startUrl);
      setCurrentIndex(index);
      setCurrentTime(0);
      void audio.play();
      setPlaying(true);
      setSessionActive(true);
      updateMediaSession(track, {
        album: opts.albumName,
        artist: opts.artist,
        artwork: opts.artwork,
      });
      // Replace radio day-clock immediately so lock screen shows catalog duration.
      livePositionArmedRef.current = '';
      publishOnDemandPositionState(audio, track, positionCacheRef);
    },
    [updateMediaSession, useAlbumAudio],
  );

  const toggleTrack = useCallback(
    (
      position: number,
      opts?: { shuffle?: boolean; adoptPending?: boolean },
    ) => {
      const adoptPending = Boolean(opts?.adoptPending);

      // Radio: pause/resume; album track clicks exit radio. Live resume rejoins clock.
      if (radioModeRef.current) {
        if (adoptPending) {
          const pending = pendingReleaseRef.current;
          if (pending) {
            const albumTrack = pending.tracks.find(
              (t) => t.position === position,
            );
            if (albumTrack && isPlayable(albumTrack)) {
              exitRadioToRelease(pending, position, Boolean(opts?.shuffle));
              return;
            }
          }
        }
        const audio = ensureRadioAudioElement(
          albumAudioRef,
          radioAudioRef,
          audioRef,
        );
        if (!audio.paused) {
          wantPlayingRef.current = false;
          audio.pause();
          setPlaying(false);
          return;
        }
        if (radioTimeshiftRef.current > 0 && audio.src) {
          wantPlayingRef.current = true;
          void audio.play().then(
            () => {
              setPlaying(true);
              setSessionActive(true);
            },
            () => void loadRadioAtPlayhead(),
          );
          return;
        }
        void loadRadioAtPlayhead({ timeshiftMs: 0 });
        return;
      }

      // Browsed a different release while this session plays — swap only when
      // the user explicitly picks a track on that release (not sheet pause/skip).
      if (adoptPending) {
        const pending = pendingReleaseRef.current;
        if (pending) {
          const sameRelease =
            Boolean(pending.releaseId) && pending.releaseId === releaseId;
          if (sameRelease) {
            // Already playing this album — clear stale pending; toggle below.
            pendingReleaseRef.current = null;
          } else {
            const albumTrack = pending.tracks.find(
              (t) => t.position === position,
            );
            if (albumTrack && isPlayable(albumTrack)) {
              exitRadioToRelease(pending, position, Boolean(opts?.shuffle));
              return;
            }
          }
        }
      }

      const index = tracks.findIndex((t) => t.position === position);
      if (index < 0) return;
      const track = tracks[index];
      if (!isPlayable(track)) return;
      setShuffleMode(Boolean(opts?.shuffle));

      const audio = useAlbumAudio();
      ensureAudioFx(audio);
      void resumeAudioFx();

      const url = mediaUrl(track.streamUrl);
      const same = audio.src && mediaUrl(audio.src) === url;

      if (same) {
        if (audio.paused) {
          // Track row / resume must replace an active music video, not layer under it.
          audioTakesOverFromVideo();
          wantPlayingRef.current = true;
          void audio.play();
          setPlaying(true);
          setSessionActive(true);
        } else {
          wantPlayingRef.current = false;
          audio.pause();
          setPlaying(false);
        }
      } else {
        audioTakesOverFromVideo();
        wantPlayingRef.current = true;
        audio.src = url;
        rememberOfflineAudio(url);
        setCurrentIndex(index);
        setCurrentTime(0);
        void audio.play();
        setPlaying(true);
        setSessionActive(true);
      }

      updateMediaSession(track);
      publishFinitePosition(audio);
    },
    [
      tracks,
      releaseId,
      updateMediaSession,
      exitRadioToRelease,
      loadRadioAtPlayhead,
      useAlbumAudio,
      publishFinitePosition,
    ],
  );

  const playAlbum = useCallback(() => {
    if (radioModeRef.current) {
      const pending = pendingReleaseRef.current;
      if (pending) {
        const first = pending.tracks.find(isPlayable);
        if (first) exitRadioToRelease(pending, first.position);
        return;
      }
      const audio = audioRef.current;
      if (audio && !audio.paused) {
        wantPlayingRef.current = false;
        audio.pause();
        setPlaying(false);
      } else if (radioTimeshiftRef.current > 0 && audio?.src) {
        wantPlayingRef.current = true;
        void audio.play();
      } else {
        void loadRadioAtPlayhead({ timeshiftMs: 0 });
      }
      return;
    }
    // Play the browsed release even if another album is still the active session.
    const pending = pendingReleaseRef.current;
    if (pending) {
      const first = pending.tracks.find(isPlayable);
      if (first) exitRadioToRelease(pending, first.position);
      return;
    }
    setShuffleMode(false);
    if (sessionActive && tracks[currentIndex] && isPlayable(tracks[currentIndex])) {
      toggleTrack(tracks[currentIndex].position);
      return;
    }
    const first = tracks.find(isPlayable);
    if (first) toggleTrack(first.position);
  }, [
    sessionActive,
    tracks,
    currentIndex,
    toggleTrack,
    exitRadioToRelease,
    loadRadioAtPlayhead,
  ]);

  const shufflePlay = useCallback(() => {
    const pending = pendingReleaseRef.current;
    if (radioModeRef.current || pending) {
      if (!pending) return;
      const playable = pending.tracks.filter(isPlayable);
      if (!playable.length) return;
      const pick = playable[Math.floor(Math.random() * playable.length)]!;
      exitRadioToRelease(pending, pick.position, true);
      return;
    }
    const playable = tracks
      .map((track, index) => ({ track, index }))
      .filter(({ track }) => isPlayable(track));
    if (!playable.length) return;
    const pick = playable[Math.floor(Math.random() * playable.length)]!;
    toggleTrack(pick.track.position, { shuffle: true });
  }, [tracks, toggleTrack, exitRadioToRelease]);

  const playNext = useCallback(() => {
    if (radioModeRef.current) {
      if (radioTimeshiftRef.current <= RADIO_SKIP_STEP_MS) {
        jumpToLive();
      } else {
        skipRadioBy(RADIO_SKIP_STEP_MS);
      }
      return;
    }
    if (!tracks.length) return;
    if (shuffleMode) {
      const playable = tracks
        .map((track, index) => ({ track, index }))
        .filter(({ track, index }) => isPlayable(track) && index !== currentIndex);
      if (!playable.length) {
        const only = tracks.find(isPlayable);
        if (only) toggleTrack(only.position, { shuffle: true });
        return;
      }
      const pick = playable[Math.floor(Math.random() * playable.length)]!;
      setCurrentIndex(pick.index);
      toggleTrack(pick.track.position, { shuffle: true });
      return;
    }

    let next = -1;
    for (let i = currentIndex + 1; i < tracks.length; i++) {
      if (isPlayable(tracks[i]!)) {
        next = i;
        break;
      }
    }
    if (next < 0) {
      if (repeatMode === 'off') {
        const audio = audioRef.current;
        // Queue finished — clear intent so lock-screen wake does not restart.
        wantPlayingRef.current = false;
        if (audio) {
          audio.pause();
          audio.currentTime = 0;
        }
        setPlaying(false);
        setCurrentTime(0);
        return;
      }
      for (let i = 0; i < currentIndex; i++) {
        if (isPlayable(tracks[i]!)) {
          next = i;
          break;
        }
      }
    }
    if (next < 0) return;
    setCurrentIndex(next);
    toggleTrack(tracks[next]!.position);
  }, [
    tracks,
    currentIndex,
    toggleTrack,
    shuffleMode,
    repeatMode,
    jumpToLive,
    skipRadioBy,
  ]);

  const playPrevious = useCallback(() => {
    if (radioModeRef.current) {
      skipRadioBy(-RADIO_SKIP_STEP_MS);
      return;
    }
    if (!tracks.length) return;

    const restartCurrent = () => {
      seek(0);
    };

    const playableCount = tracks.reduce(
      (n, t) => n + (isPlayable(t) ? 1 : 0),
      0,
    );
    // Singles: Previous always restarts (no prior track to skip to).
    if (playableCount <= 1) {
      restartCurrent();
      return;
    }

    const wrap = repeatMode !== 'off';
    const prevTrack = findNeighborPlayable(tracks, currentIndex, -1, wrap);
    // First playable track + repeat off: always restart, regardless of time.
    if (!prevTrack) {
      restartCurrent();
      return;
    }

    const timeSec = audioRef.current?.currentTime ?? currentTime;
    // Past the threshold → restart; early in the track → go previous (or wrap).
    if (timeSec > PREV_RESTART_THRESHOLD_SEC) {
      restartCurrent();
      return;
    }

    const prevIndex = tracks.indexOf(prevTrack);
    if (prevIndex < 0) return;
    setCurrentIndex(prevIndex);
    toggleTrack(
      prevTrack.position,
      shuffleMode ? { shuffle: true } : undefined,
    );
  }, [
    tracks,
    currentIndex,
    currentTime,
    toggleTrack,
    shuffleMode,
    skipRadioBy,
    seek,
    repeatMode,
  ]);

  useEffect(() => {
    // Bind both elements so switching album ↔ radio never drops handlers.
    const album = ensureAudioElement(albumAudioRef);
    const radio = ensureAudioElement(radioAudioRef);
    if (!audioRef.current) audioRef.current = album;
    const els = [album, radio];

    const onTime = (event: Event) => {
      const audio = event.currentTarget as HTMLAudioElement;
      if (audio !== audioRef.current) return;
      // Local scrub owns the playhead while the pointer is down.
      if (seekScrubbingRef.current) return;
      const pending = pendingSeekTargetRef.current;
      if (pending != null) {
        // Ignore stale ticks until the element reaches the drop target.
        if (audio.seeking) return;
        if (Math.abs(audio.currentTime - pending) > 0.35) return;
        clearPendingSeek(pending);
      } else if (audio.seeking) {
        return;
      }
      setCurrentTime(audio.currentTime);
      // Radio day clock is armed on track/play/pause — not on a 1Hz timer
      // (macOS Control Center artwork thrash).
      if (radioModeRef.current) return;
      const now = performance.now();
      if (now - lastPositionStateRef.current < 1000) return;
      lastPositionStateRef.current = now;
      syncMediaPosition(audio);
    };
    const onEnded = (event: Event) => {
      const audio = event.currentTarget as HTMLAudioElement;
      if (audio !== audioRef.current) return;
      if (radioModeRef.current) {
        // HLS day-loop rarely ends (multi-copy VOD). Progressive needs a reload.
        // forceReload rebuilds the playlist / next MP3 at the live clock.
        void loadRadioAtPlayhead({ forceReload: true });
        return;
      }
      if (repeatModeRef.current === 'one') {
        audio.currentTime = 0;
        void audio.play();
        return;
      }
      playNext();
    };
    const onPlay = (event: Event) => {
      const audio = event.currentTarget as HTMLAudioElement;
      if (audio !== audioRef.current) return;
      wantPlayingRef.current = true;
      setPlaying(true);
      setSessionActive(true);
      if ('mediaSession' in navigator && !isVideoSessionActive()) {
        try {
          navigator.mediaSession.playbackState = 'playing';
        } catch {
          /* unsupported */
        }
      }
      // Force day clock (radio) or finite duration (on-demand) as playback starts.
      if (!isVideoSessionActive()) {
        syncMediaPosition(audio, { forceLive: true });
      }
    };
    const onPause = (event: Event) => {
      const audio = event.currentTarget as HTMLAudioElement;
      if (audio !== audioRef.current) return;
      // Ignore stalls / source swaps — only treat as paused when the user stopped.
      if (wantPlayingRef.current) return;
      setPlaying(false);
      if ('mediaSession' in navigator && !isVideoSessionActive()) {
        try {
          navigator.mediaSession.playbackState = 'paused';
        } catch {
          /* unsupported */
        }
      }
      // timeupdate stops while paused — republish so iOS keeps times / day clock.
      if (!isVideoSessionActive()) {
        syncMediaPosition(audio, { forceLive: true });
      }
    };
    const onLoadedMeta = (event: Event) => {
      const audio = event.currentTarget as HTMLAudioElement;
      if (audio !== audioRef.current) return;
      syncMediaPosition(audio, { forceLive: true });
    };
    const onSeeked = (event: Event) => {
      const audio = event.currentTarget as HTMLAudioElement;
      if (audio !== audioRef.current) return;
      const pending = pendingSeekTargetRef.current;
      if (pending != null && Math.abs(audio.currentTime - pending) <= 0.5) {
        clearPendingSeek(pending);
      }
      if (seekScrubbingRef.current) {
        syncMediaPosition(audio, { forceLive: true });
        return;
      }
      setCurrentTime(audio.currentTime);
      syncMediaPosition(audio, { forceLive: true });
    };

    for (const audio of els) {
      audio.addEventListener('timeupdate', onTime);
      audio.addEventListener('ended', onEnded);
      audio.addEventListener('play', onPlay);
      audio.addEventListener('pause', onPause);
      audio.addEventListener('loadedmetadata', onLoadedMeta);
      audio.addEventListener('seeked', onSeeked);
    }
    return () => {
      for (const audio of els) {
        audio.removeEventListener('timeupdate', onTime);
        audio.removeEventListener('ended', onEnded);
        audio.removeEventListener('play', onPlay);
        audio.removeEventListener('pause', onPause);
        audio.removeEventListener('loadedmetadata', onLoadedMeta);
        audio.removeEventListener('seeked', onSeeked);
      }
    };
  }, [playNext, loadRadioAtPlayhead, syncMediaPosition, clearPendingSeek]);

  // Resume after lock-screen / tab freeze if we still intend to play.
  // Do NOT rejoin on every window focus — that constantly seeks/skips on desktop.
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        radioWasHiddenRef.current = true;
        return;
      }
      if (!radioWasHiddenRef.current) return;
      radioWasHiddenRef.current = false;

      if (radioModeRef.current) {
        if (wantPlayingRef.current) {
          const audio = audioRef.current;
          // HLS keeps playing under the lock — only rejoin if it actually stopped.
          if (
            radioHlsActiveRef.current &&
            audio &&
            !audio.paused &&
            !audio.ended
          ) {
            syncRadioMetadata();
            return;
          }
          if (audio?.paused && radioHlsActiveRef.current) {
            void audio.play().then(
              () => {
                setPlaying(true);
                syncRadioMetadata();
              },
              () => void loadRadioAtPlayhead({ forceReload: true }),
            );
            return;
          }
          void loadRadioAtPlayhead({ forceReload: true });
        } else if (sessionActiveRef.current) {
          syncRadioMetadata();
        }
        return;
      }

      if (!wantPlayingRef.current) return;

      const audio = albumAudioRef.current ?? audioRef.current;
      if (!audio) return;
      if (audio.ended) {
        wantPlayingRef.current = false;
        setPlaying(false);
        return;
      }
      void resumeAudioFx();
      if (audio.paused) {
        void audio.play().catch(() => {
          /* user gesture / network — ignore */
        });
      }
      syncMediaPosition(audio);
    };
    const onPageShow = (event: PageTransitionEvent) => {
      // bfcache restore only — normal navigations use visibilitychange.
      if (!event.persisted) return;
      radioWasHiddenRef.current = true;
      onVisibility();
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pageshow', onPageShow);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pageshow', onPageShow);
    };
  }, [loadRadioAtPlayhead, syncMediaPosition, syncRadioMetadata]);

  // While paused on-demand, periodically re-publish finite position so the
  // lock screen doesn’t fall back to --:-- after iOS idles the session.
  useEffect(() => {
    if (!sessionActive || playing || radioMode) return;
    const tick = () => {
      if (radioModeRef.current) return;
      syncMediaPosition(audioRef.current);
    };
    tick();
    const id = window.setInterval(tick, 5000);
    return () => window.clearInterval(id);
  }, [sessionActive, playing, radioMode, syncMediaPosition]);

  // Re-enter buffering when the live stream waits / stalls; clear once audio
  // is moving again. iOS HLS often fires `waiting` without a matching `playing`
  // when the pipeline resumes — also clear on canplay / timeupdate.
  // Progressive next-track stitches suppress BUFFERING entirely (LIVE stays put);
  // only unexpected mid-stream stalls after the stitch ends flip the badge.
  useEffect(() => {
    if (!radioMode) return;
    const audio = radioAudioRef.current ?? audioRef.current;
    if (!audio) return;
    const clearWaitTimer = () => {
      if (radioBufferingWaitTimerRef.current != null) {
        window.clearTimeout(radioBufferingWaitTimerRef.current);
        radioBufferingWaitTimerRef.current = null;
      }
    };
    const clearBuffering = () => {
      clearWaitTimer();
      if (!radioBufferingRef.current) return;
      radioBufferingRef.current = false;
      setRadioBuffering(false);
    };
    const endSeamlessStitch = () => {
      if (!radioSeamlessStitchRef.current) return;
      if (audio.readyState < HTMLMediaElement.HAVE_FUTURE_DATA) return;
      radioSeamlessStitchRef.current = false;
      if (radioSeamlessStitchTimerRef.current != null) {
        window.clearTimeout(radioSeamlessStitchTimerRef.current);
        radioSeamlessStitchTimerRef.current = null;
      }
    };
    const onWaiting = () => {
      if (!wantPlayingRef.current || audio.ended) return;
      if (radioBufferingRef.current) return;
      // Intentional src swap / next MP3 stitch — do not blink LIVE → BUFFERING.
      if (radioLoadInFlightRef.current || radioSeamlessStitchRef.current) {
        clearWaitTimer();
        return;
      }
      if (radioBufferingWaitTimerRef.current != null) return;
      radioBufferingWaitTimerRef.current = window.setTimeout(() => {
        radioBufferingWaitTimerRef.current = null;
        // Stall resolved before the debounce elapsed.
        if (!wantPlayingRef.current || audio.paused || audio.ended) return;
        if (radioLoadInFlightRef.current || radioSeamlessStitchRef.current) return;
        if (audio.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) return;
        radioBufferingRef.current = true;
        setRadioBuffering(true);
      }, 2000);
    };
    const onReady = () => {
      if (!audio.paused && wantPlayingRef.current) {
        endSeamlessStitch();
        clearBuffering();
      }
    };
    const onTime = () => {
      if (!audio.paused && wantPlayingRef.current) {
        endSeamlessStitch();
        clearBuffering();
      }
    };
    audio.addEventListener('waiting', onWaiting);
    audio.addEventListener('stalled', onWaiting);
    audio.addEventListener('playing', onReady);
    audio.addEventListener('canplay', onReady);
    audio.addEventListener('canplaythrough', onReady);
    audio.addEventListener('timeupdate', onTime);
    return () => {
      clearWaitTimer();
      audio.removeEventListener('waiting', onWaiting);
      audio.removeEventListener('stalled', onWaiting);
      audio.removeEventListener('playing', onReady);
      audio.removeEventListener('canplay', onReady);
      audio.removeEventListener('canplaythrough', onReady);
      audio.removeEventListener('timeupdate', onTime);
    };
  }, [radioMode]);

  // Soft listener presence → dedicated presence Worker (KV). Sparse heartbeats.
  useEffect(() => {
    if (!radioMode || !playing) return;
    const ping = () => {
      const id = tracks[currentIndex]?.id;
      void touchRadioPresence(id);
    };
    ping();
    const timer = window.setInterval(ping, RADIO_PRESENCE_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [radioMode, playing, tracks, currentIndex]);

  // Warm prev/next playable tracks while listening to a release.
  useEffect(() => {
    if (radioMode || !sessionActive || !tracks.length) {
      if (!radioMode) {
        clearPrefetchSlot(prevPrefetchRef);
        clearPrefetchSlot(nextPrefetchRef);
      }
      return;
    }

    const mainSrc = audioRef.current?.src
      ? mediaUrl(audioRef.current.src)
      : '';
    const wrap = repeatMode === 'all';
    const prev = findNeighborPlayable(tracks, currentIndex, -1, wrap);
    const next = findNeighborPlayable(tracks, currentIndex, 1, wrap);

    warmPrefetchSlot(
      prevPrefetchRef,
      prev?.streamUrl ? mediaUrl(prev.streamUrl) : '',
      mainSrc,
    );
    warmPrefetchSlot(
      nextPrefetchRef,
      next?.streamUrl ? mediaUrl(next.streamUrl) : '',
      mainSrc,
    );
  }, [radioMode, sessionActive, tracks, currentIndex, repeatMode]);

  // Metadata + recovery. HLS advances tracks in the OS — only refresh chrome.
  // Progressive still reloads on track boundary / large drift / long stall.
  useEffect(() => {
    if (!radioMode) return;
    clearPrefetchSlot(prevPrefetchRef);
    let lastDriftReloadAt = 0;
    let pausedSince = 0;
    const tick = () => {
      if (radioLoadInFlightRef.current) return;
      const schedule = radioScheduleRef.current;
      if (!schedule?.tracks?.length) return;
      const pos = resolveRadioPosition(
        schedule.tracks,
        schedule.epochMs,
        radioPlayNowMs(radioSkewRef.current, radioTimeshiftRef.current),
      );
      if (!pos) return;

      const audio = audioRef.current;
      const hls = radioHlsActiveRef.current;

      // Chrome follows the live clock (metadata only).
      if (pos.index !== radioLoadedIndexRef.current) {
        radioLoadedIndexRef.current = pos.index;
        syncRadioUi(schedule, pos.index);
        if (audio && hls) {
          applyRadioElementLoudness(audio, {
            id: pos.track.id,
            name: pos.track.name,
            duration: pos.track.duration,
            position: pos.index + 1,
            streamUrl: pos.track.streamUrl,
            replayGain: pos.track.replayGain,
            replayGainTrack: pos.track.replayGainTrack,
            replayGainAlbum: pos.track.replayGainAlbum,
          });
        }
      }

      if (!wantPlayingRef.current) return;
      if (!audio) return;

      // HLS: OS owns the stream — never swap src on track change.
      if (hls) {
        if (audio.ended) {
          void loadRadioAtPlayhead({ forceReload: true });
          return;
        }
        if (!audio.paused) {
          // Clear sticky BUFFERING if waiting never got a matching playing.
          if (radioBufferingRef.current) {
            radioBufferingRef.current = false;
            setRadioBuffering(false);
          }
          pausedSince = 0;
          return;
        }
        const now = Date.now();
        if (!pausedSince) pausedSince = now;
        if (now - pausedSince > 8_000) {
          pausedSince = 0;
          void audio.play().then(
            () => {
              setPlaying(true);
              radioBufferingRef.current = false;
              setRadioBuffering(false);
            },
            () => void loadRadioAtPlayhead({ forceReload: true }),
          );
        }
        return;
      }

      // Progressive path: stitch next MP3 when the schedule advances.
      if (pos.index !== currentIndexRef.current) {
        void loadRadioAtPlayhead();
        return;
      }

      if (audio.ended) {
        void loadRadioAtPlayhead({ forceReload: true });
        return;
      }

      if (!audio.paused) {
        if (radioBufferingRef.current) {
          radioBufferingRef.current = false;
          setRadioBuffering(false);
        }
        pausedSince = 0;
      } else {
        const now = Date.now();
        if (!pausedSince) pausedSince = now;
        if (now - pausedSince > 8_000) {
          pausedSince = 0;
          void loadRadioAtPlayhead({ forceReload: true });
        }
        return;
      }

      const drift = Math.abs(audio.currentTime - pos.offsetSec);
      if (drift > RADIO_DRIFT_RELOAD_SEC) {
        const now = Date.now();
        if (now - lastDriftReloadAt > 30_000) {
          lastDriftReloadAt = now;
          void loadRadioAtPlayhead({ forceReload: true });
        }
        return;
      }

      if (pos.remainingSec <= 15 && pos.remainingSec > 0) {
        const next = schedule.tracks[(pos.index + 1) % schedule.tracks.length];
        if (next?.streamUrl) {
          warmPrefetchSlot(nextPrefetchRef, mediaUrl(next.streamUrl));
        }
        // Warm cover before stitch so the footer art can crossfade without a flash.
        if (next?.artworkUrl) {
          void preloadImageUrl(artworkUrl(next.artworkUrl));
        }
      }
      if (radioTimeshiftRef.current > 0) {
        const prevIdx =
          (pos.index - 1 + schedule.tracks.length) % schedule.tracks.length;
        const prev = schedule.tracks[prevIdx];
        if (prev?.streamUrl) {
          warmPrefetchSlot(prevPrefetchRef, mediaUrl(prev.streamUrl));
        }
        if (prev?.artworkUrl) {
          void preloadImageUrl(artworkUrl(prev.artworkUrl));
        }
      }
    };

    const id = window.setInterval(tick, 5000);
    return () => window.clearInterval(id);
  }, [radioMode, loadRadioAtPlayhead, syncRadioUi]);

  useEffect(() => {
    if (!('mediaSession' in navigator)) return;

    const bindAudioHandlers = () => {
      // Music video owns Media Session while active (lock screen / background).
      if (isVideoSessionActive()) return;

      navigator.mediaSession.setActionHandler('play', () => {
        if (isVideoSessionActive()) return;
        if (radioModeRef.current) {
          wantPlayingRef.current = true;
          if (radioTimeshiftRef.current > 0 && audioRef.current?.src) {
            void audioRef.current.play().then(
              () => undefined,
              () => void loadRadioAtPlayhead(),
            );
          } else {
            void loadRadioAtPlayhead({ timeshiftMs: 0 });
          }
          return;
        }
        void audioRef.current?.play();
      });
      navigator.mediaSession.setActionHandler('pause', () => {
        if (isVideoSessionActive()) return;
        wantPlayingRef.current = false;
        audioRef.current?.pause();
      });

      // Lock-screen / Control Center:
      // - On-demand releases → next/previous track only
      // - Live radio → seek ± only (always both sides so iOS doesn't fall back
      //   to nexttrack when seekforward is missing at the live edge)
      if (radioModeRef.current) {
        navigator.mediaSession.setActionHandler('previoustrack', null);
        navigator.mediaSession.setActionHandler('nexttrack', null);
        navigator.mediaSession.setActionHandler('seekbackward', (details) => {
          if (isVideoSessionActive()) return;
          skipRadioBy(-Math.round((details.seekOffset ?? 15) * 1000));
        });
        navigator.mediaSession.setActionHandler('seekforward', (details) => {
          if (isVideoSessionActive()) return;
          if (radioTimeshiftRef.current <= 0) return;
          skipRadioBy(Math.round((details.seekOffset ?? 15) * 1000));
        });
        // Day-clock position ≠ element currentTime — scrubbing would seek wrong.
        navigator.mediaSession.setActionHandler('seekto', null);
      } else {
        navigator.mediaSession.setActionHandler('seekforward', null);
        navigator.mediaSession.setActionHandler('seekbackward', null);
        navigator.mediaSession.setActionHandler('previoustrack', () => {
          if (isVideoSessionActive()) return;
          playPrevious();
        });
        navigator.mediaSession.setActionHandler('nexttrack', () => {
          if (isVideoSessionActive()) return;
          playNext();
        });
        navigator.mediaSession.setActionHandler('seekto', (details) => {
          if (isVideoSessionActive()) return;
          if (details.seekTime != null) seek(details.seekTime);
        });
      }
    };

    bindAudioHandlers();
    window.addEventListener(VIDEO_SESSION_EVENT, bindAudioHandlers);

    return () => {
      window.removeEventListener(VIDEO_SESSION_EVENT, bindAudioHandlers);
      if (!('mediaSession' in navigator)) return;
      if (isVideoSessionActive()) return;
      navigator.mediaSession.setActionHandler('play', null);
      navigator.mediaSession.setActionHandler('pause', null);
      navigator.mediaSession.setActionHandler('previoustrack', null);
      navigator.mediaSession.setActionHandler('nexttrack', null);
      navigator.mediaSession.setActionHandler('seekto', null);
      navigator.mediaSession.setActionHandler('seekforward', null);
      navigator.mediaSession.setActionHandler('seekbackward', null);
    };
  }, [
    radioMode,
    radioTimeshiftMs,
    playNext,
    playPrevious,
    loadRadioAtPlayhead,
    skipRadioBy,
    seek,
  ]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const active = document.activeElement as HTMLElement | null;
      const isText =
        active?.tagName === 'TEXTAREA' ||
        (active?.tagName === 'INPUT' &&
          (active.getAttribute('type') === 'text' || active.getAttribute('type') === 'email'));
      if (event.code === 'Space' && !isText) {
        event.preventDefault();
        if (radioModeRef.current) {
          const audio = audioRef.current;
          if (audio && !audio.paused) {
            wantPlayingRef.current = false;
            audio.pause();
            setPlaying(false);
          } else if (radioTimeshiftRef.current > 0 && audio?.src) {
            wantPlayingRef.current = true;
            void audio.play();
          } else {
            void loadRadioAtPlayhead({ timeshiftMs: 0 });
          }
          return;
        }
        const track = tracks[currentIndex] || tracks.find(isPlayable);
        if (track) toggleTrack(track.position);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [tracks, currentIndex, toggleTrack, loadRadioAtPlayhead]);

  const value = useMemo(
    () => ({
      audioRef,
      tracks,
      currentIndex,
      releaseId,
      albumName,
      artist,
      releaseType,
      artwork,
      credits,
      streamLinks,
      playing,
      sessionActive,
      shuffleMode,
      repeatMode,
      radioMode,
      radioBuffering,
      radioTimeshiftMs,
      radioClockTimezone,
      setReleasePlayback,
      toggleTrack,
      playAlbum,
      shufflePlay,
      toggleShuffle,
      cycleRepeat,
      playNext,
      playPrevious,
      startRadio,
      jumpToLive,
      currentTime,
      seek,
      setSeekScrubbing,
    }),
    [
      tracks,
      currentIndex,
      releaseId,
      albumName,
      artist,
      releaseType,
      artwork,
      credits,
      streamLinks,
      playing,
      sessionActive,
      shuffleMode,
      repeatMode,
      radioMode,
      radioBuffering,
      radioTimeshiftMs,
      radioClockTimezone,
      setReleasePlayback,
      toggleTrack,
      playAlbum,
      shufflePlay,
      toggleShuffle,
      cycleRepeat,
      playNext,
      playPrevious,
      startRadio,
      jumpToLive,
      currentTime,
      seek,
      setSeekScrubbing,
    ],
  );

  return <PlayerContext.Provider value={value}>{children}</PlayerContext.Provider>;
}

export function usePlayer() {
  const ctx = useContext(PlayerContext);
  if (!ctx) throw new Error('usePlayer outside provider');
  return ctx;
}

export type SelectedMusic = {
  release: Release;
  openId: string;
};

export type SelectedPhoto = {
  post: PhotoPost;
  index: number;
};

export type AppSelection = {
  music: SelectedMusic | null;
  photo: SelectedPhoto | null;
};

/** Minimum catalog row height (padding + line + 1px border) before fitting more rows. */
export const CATALOG_MIN_ROW_HEIGHT = 28;

export type CatalogListLayout = {
  rowCount: number;
  rowHeight: number;
  bandHeight: number;
};

function estimateCatalogRows(): number {
  if (typeof window === 'undefined') return 12;
  const band = Math.max(200, window.innerHeight - 160);
  return Math.max(1, Math.floor(band / CATALOG_MIN_ROW_HEIGHT));
}

/**
 * Lock list + media to the same flex band height (Infos-style 20px gaps).
 * Fits an integer number of equal-height rows to the viewport band.
 * Also marks snap-edge rows so outer borders don't double the header/footer rules.
 *
 * Pass `equalRows: false` for focus/detail mode (no snap row sizing).
 */
export function useBalancedList(
  listRef: React.RefObject<HTMLElement | null>,
  itemCount: number,
  options?: { equalRows?: boolean },
): CatalogListLayout {
  const equalRows = options?.equalRows !== false;
  const [layout, setLayout] = useState<CatalogListLayout>(() => {
    const rowCount = estimateCatalogRows();
    return { rowCount, rowHeight: CATALOG_MIN_ROW_HEIGHT, bandHeight: rowCount * CATALOG_MIN_ROW_HEIGHT };
  });

  useEffect(() => {
    const el = listRef.current;
    if (!el) return;

    const measureBand = () => {
      const left = el.closest('.left-column') as HTMLElement | null;
      if (!left) return el.clientHeight;
      const header = left.querySelector(':scope > .column-header') as HTMLElement | null;
      const footer = left.querySelector(':scope > .column-footer') as HTMLElement | null;
      const cs = getComputedStyle(left);
      const pad = (parseFloat(cs.paddingTop) || 0) + (parseFloat(cs.paddingBottom) || 0);
      const headerH = header
        ? header.getBoundingClientRect().height +
          (parseFloat(getComputedStyle(header).marginBottom) || 0)
        : 0;
      const footerH = footer
        ? footer.getBoundingClientRect().height +
          (parseFloat(getComputedStyle(footer).marginTop) || 0)
        : 0;
      return Math.max(0, left.clientHeight - pad - headerH - footerH);
    };

    /**
     * Hide separators on the scrollport edges:
     * - last visible row → no bottom border (clears the line above the footer gap)
     * - row above the top visible row → no bottom border (clears the line at list top when snapped)
     */
    const updateSnapEdges = () => {
      const items = [...el.querySelectorAll<HTMLElement>(':scope > .release-item')];
      if (!items.length) return;
      const port = el.getBoundingClientRect();
      const topEdge = port.top;
      const bottomEdge = port.bottom;
      const visible: HTMLElement[] = [];

      for (const item of items) {
        item.classList.remove('edge-bottom', 'edge-cap');
        const r = item.getBoundingClientRect();
        // Fully-in or clipped rows that still occupy the scrollport.
        if (r.bottom > topEdge + 0.5 && r.top < bottomEdge - 0.5) {
          visible.push(item);
        }
      }
      if (!visible.length) return;

      const topItem = visible[0]!;
      const bottomItem = visible[visible.length - 1]!;
      bottomItem.classList.add('edge-bottom');

      // Separator belonging to the row just above the fold (= visual "top border" of the top row).
      const prev = topItem.previousElementSibling;
      if (prev instanceof HTMLElement && prev.classList.contains('release-item')) {
        prev.classList.add('edge-cap');
      }
    };

    const fit = () => {
      const h = measureBand();
      if (h <= 0) return;
      const next = `${h}px`;
      const root = el.closest('.container') as HTMLElement | null;
      el.style.paddingBottom = '';
      el.style.minHeight = '0';

      // Band height via CSS var so React style updates on .gradient-fill
      // (portrait zoom/focus vars) never wipe the locked media frame.
      if (root) root.style.setProperty('--catalog-band-height', next);
      el.style.flexGrow = '0';
      el.style.height = next;

      if (equalRows) {
        const rowCount = Math.max(1, Math.floor(h / CATALOG_MIN_ROW_HEIGHT));
        const rowHeight = h / rowCount;
        el.style.setProperty('--catalog-row-height', `${rowHeight}px`);
        el.style.scrollSnapType = itemCount > 0 ? 'y proximity' : '';
        setLayout((prev) =>
          prev.rowCount === rowCount &&
          Math.abs(prev.rowHeight - rowHeight) < 0.01 &&
          Math.abs(prev.bandHeight - h) < 0.01
            ? prev
            : { rowCount, rowHeight, bandHeight: h },
        );
      } else {
        el.style.scrollSnapType = 'none';
        setLayout((prev) => {
          const rowHeight = prev.rowHeight || CATALOG_MIN_ROW_HEIGHT;
          el.style.setProperty('--catalog-row-height', `${rowHeight}px`);
          if (Math.abs(prev.bandHeight - h) < 0.01) return prev;
          return { rowCount: prev.rowCount, rowHeight, bandHeight: h };
        });
      }

      updateSnapEdges();
    };

    let edgeRaf = 0;
    const onScroll = () => {
      if (edgeRaf) return;
      edgeRaf = requestAnimationFrame(() => {
        edgeRaf = 0;
        updateSnapEdges();
      });
    };

    fit();
    updateSnapEdges();
    // After snap settles (and after layout/fonts), re-measure edges.
    requestAnimationFrame(() => requestAnimationFrame(updateSnapEdges));
    const ro = new ResizeObserver(fit);
    const left = el.closest('.left-column');
    if (left) ro.observe(left);
    const mo = new MutationObserver(() => {
      updateSnapEdges();
    });
    mo.observe(el, { childList: true, subtree: true });
    el.addEventListener('scroll', onScroll, { passive: true });
    el.addEventListener('scrollend', updateSnapEdges);
    window.addEventListener('resize', fit);
    return () => {
      ro.disconnect();
      mo.disconnect();
      el.removeEventListener('scroll', onScroll);
      el.removeEventListener('scrollend', updateSnapEdges);
      window.removeEventListener('resize', fit);
      if (edgeRaf) cancelAnimationFrame(edgeRaf);
      const root = el.closest('.container') as HTMLElement | null;
      root?.style.removeProperty('--catalog-band-height');
      el.style.paddingBottom = '';
      el.style.scrollSnapType = '';
      el.style.height = '';
      el.style.flexGrow = '';
      el.style.removeProperty('--catalog-row-height');
      el.querySelectorAll('.release-item.edge-bottom, .release-item.edge-cap').forEach((item) => {
        item.classList.remove('edge-bottom', 'edge-cap');
      });
    };
  }, [listRef, itemCount, equalRows]);

  return layout;
}

export type { PhotoMedia, PhotoPost, Release, Track };
